import { FC, useContext, useEffect, useState } from "react";
import Image from "next/image";
import { ChevronDown, ChevronUp, QuestionIcon } from "components/atoms/Icon";
import { Input } from "components/atoms/Input";
import { Select } from "components/atoms/Select";
import { Toggle } from "components/atoms/Toggle";
import { EnhancedModelTooltip } from "components/molecules/Tooltip";
import { getModelConfig } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, IFreeGenModelDTO, IModelConfig, UserPremiumType } from "types";
import * as Styled from "./EnhancedModelControl.styled";
interface EnhancedModelControlProps {
  config: IFreeGenModelDTO;
  onConfigChange: (config: Partial<IFreeGenModelDTO>) => void;
  useCustomConfig: boolean;
  setUseCustomConfig: (value: boolean) => void;
}
const EnhancedModelControl: FC<EnhancedModelControlProps> = ({
  config,
  onConfigChange,
  useCustomConfig,
  setUseCustomConfig
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const isSubscribed = state.userPremiumLevel !== UserPremiumType.NONE;
  const [modelConfigs, setModelConfigs] = useState<IModelConfig[]>([]);
  const [samplers, setSamplers] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      const data = await getModelConfig();
      setModelConfigs(data.modelConfigs);
      setSamplers(data.samplers);
    };
    fetchData();
  }, []);
  const handleUseCustomModelToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setUseCustomConfig(e.target.checked);
    if (!e.target.checked) {
      const defaultModel = modelConfigs.find(model => model.modelName === config.model);
      if (defaultModel) {
        onConfigChange({
          cfg: defaultModel.defaultCfgScale.toString(),
          steps: defaultModel.defaultSteps.toString(),
          sampler: defaultModel.defaultSampler
        });
      }
    }
  };
  const handleCfgScaleChange = (value: string) => {
    onConfigChange({
      cfg: parseFloat(value).toFixed(1)
    });
  };
  const handleStepsChange = (value: string) => {
    onConfigChange({
      steps: value
    });
  };
  const handleSamplerTypeChange = (value: string) => {
    onConfigChange({
      sampler: value
    });
  };
  const toggleAccordion = () => setIsOpen(!isOpen);
  return <Styled.Accordion data-sentry-element="unknown" data-sentry-component="EnhancedModelControl" data-sentry-source-file="index.tsx">
            <EnhancedModelTooltip id="stepsTip" title="Number of Steps" details={["The number of steps determines how many iterations the model goes through to generate the image.", "More steps typically result in higher quality and more detailed images, but they take longer to generate.", "Fewer steps can produce images more quickly, but the quality and detail may be lower.", "Adjusting the number of steps allows you to find a balance between image quality and generation speed."]} isElite={isSubscribed} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <EnhancedModelTooltip id="cfgScaleTip" title="CFG Scale" details={["The CFG (Classifier-Free Guidance) scale controls the balance between following your prompt exactly or allowing more creative freedom.", "A higher CFG scale (6-8) means the AI will stick closely to what you've described in your prompt.", "A lower CFG scale (1-2) allows the AI to be more creative and might result in images that are less strictly tied to your prompt.", "Some models, especially Hyper and Lightning models, work best with lower CFG scales and fewer steps (6-10) for faster results.", "Experimenting with different CFG scales can help you find the perfect balance between precision and creativity for your needs."]} isElite={isSubscribed} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <EnhancedModelTooltip id="modelTypeTip" title="Model" details={["Models determine the overall style and characteristics of generated images.", "Each model is trained on a different set of images and excels at producing certain styles or themes.", "Models interpret your input prompt and generate images based on that information.", "Hyper and Lightning models are specialized for speed and efficiency.", "Producing results faster with fewer steps and lower CFG scales.", "Non-hyper models take longer but produce more detailed images."]} isElite={isSubscribed} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <EnhancedModelTooltip id="samplerTypeTip" title="Sampler Type" details={["Samplers use different algorithms to generate images from your prompt.", "Each sampler may produce slightly different results, affecting the final image's speed and quality.", "Some samplers are faster but might sacrifice some detail.", "Other samplers take longer but can produce more detailed and nuanced images.", "Experimenting with different samplers can help you find the one that best suits your needs for each project."]} isElite={isSubscribed} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <EnhancedModelTooltip id="modelControlIconToolTip" title="Enhanced Model Control" details={["Choose different models to change the style and output of your generated images.", "Adjust the CFG scale to balance between creativity and strict adherence to your prompt.", "Set the number of steps to control the detail and quality of the image.", "More steps mean higher quality but longer processing time.", "Select different samplers to experiment with how the image is generated."]} isElite={isSubscribed} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <Styled.AccordionHeader onClick={toggleAccordion} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <div>
                    Enhanced Model Control
                    <Styled.Icon id="modelControlIcon" aria-label="Information about Enhanced Model Control" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Image priority height={24} width={24} src={QuestionIcon} alt="Tooltip" data-tooltip-id="modelControlIconToolTip" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                    </Styled.Icon>
                </div>
                <Styled.Icon data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Image height={24} width={24} src={isOpen ? ChevronUp : ChevronDown} alt="Toggle Accordion" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                </Styled.Icon>
            </Styled.AccordionHeader>
            {isOpen && <Styled.AccordionContent>
                    <Styled.FieldGroup>
                        <Styled.Label htmlFor="useCustomModelToggle">
                            Use Custom Model Settings:
                        </Styled.Label>
                        <Toggle id="useCustomModelToggle" checked={useCustomConfig} onChange={handleUseCustomModelToggle} />
                    </Styled.FieldGroup>
                    <Styled.FieldGroup>
                        <Styled.Label htmlFor="cfgScale">
                            CFG Scale:
                            <Styled.Icon id="cfgScaleIcon" aria-label="Information about CFG Scale" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}>
                                <Image height={24} width={24} priority src={QuestionIcon} alt="Tooltip" data-tooltip-id="cfgScaleTip" />
                            </Styled.Icon>
                        </Styled.Label>
                        <Styled.SliderWrapper>
                            <Input type="range" id="cfgScale" min="1" max="20" step="0.1" value={config.cfg} onChange={e => handleCfgScaleChange(parseFloat(e.target.value).toFixed(1))} disabled={!isSubscribed || !useCustomConfig} />
                            <Input type="number" id="cfgScaleNumber" min="1" max="20" step="0.1" value={config.cfg} onChange={e => handleCfgScaleChange(parseFloat(e.target.value).toFixed(1))} disabled={!isSubscribed || !useCustomConfig} />
                        </Styled.SliderWrapper>
                    </Styled.FieldGroup>
                    <Styled.FieldGroup>
                        <Styled.Label htmlFor="steps">
                            Number of Steps:
                            <Styled.Icon id="stepsIcon" aria-label="Information about Number of Steps" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}>
                                <Image height={24} width={24} priority src={QuestionIcon} alt="Tooltip" data-tooltip-id="stepsTip" />
                            </Styled.Icon>
                        </Styled.Label>
                        <Styled.SliderWrapper>
                            <Input type="range" id="steps" min="1" max="100" value={config.steps} onChange={e => handleStepsChange(e.target.value.toString())} disabled={!isSubscribed || !useCustomConfig} />
                            <Input type="number" id="stepsNumber" min="1" max="100" value={config.steps} onChange={e => handleStepsChange(e.target.value.toString())} disabled={!isSubscribed || !useCustomConfig} />
                        </Styled.SliderWrapper>
                    </Styled.FieldGroup>
                    <Styled.FieldGroup>
                        <Styled.Label htmlFor="samplerType">
                            Sampler Type:
                            <Styled.Icon id="samplerTypeIcon" aria-label="Information about Sampler Type" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}>
                                <Image height={24} width={24} priority src={QuestionIcon} alt="Tooltip" data-tooltip-id="samplerTypeTip" />
                            </Styled.Icon>
                        </Styled.Label>
                        <Select id="samplerType" value={config.sampler} onChange={e => handleSamplerTypeChange(e.target.value)} disabled={!isSubscribed || !useCustomConfig}>
                            {samplers.map(sampler => <option key={sampler} value={sampler}>
                                    {sampler}
                                </option>)}
                        </Select>
                    </Styled.FieldGroup>
                </Styled.AccordionContent>}
        </Styled.Accordion>;
};
export default EnhancedModelControl;